import { useState, useEffect } from 'react';
import { Badge, Card } from '@safetyhub/ui';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
  Notification,
  NotificationsListResponse,
  getNotificationTypeLabel,
  getNotificationTypeColor,
  getNotificationTypeBadgeTone,
  getNotificationResourcePath,
  formatRelativeTime,
} from '../../lib/notifications';
import { extractApiError } from '../../lib/api';
import { ChevronRightIcon, CheckIcon, XIcon } from '../../components/icons';

/** 시안 `.chk-label` — 점검 툴바 토글과 같은 결. */
const CHECK_LABEL_CLASS =
  'inline-flex cursor-pointer select-none items-center gap-2 text-[13.5px] font-semibold tracking-[-0.005em] text-ink-700';
/** 시안 `.chk-label input` — 17px, brand accent. */
const CHECKBOX_CLASS = 'm-0 h-[17px] w-[17px] cursor-pointer rounded-[5px] accent-brand-600';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [meta, setMeta] = useState<NotificationsListResponse['meta'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, [showUnreadOnly]);

  const loadNotifications = async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await getNotifications({
        unreadOnly: showUnreadOnly,
        limit: 50,
      });
      setNotifications(result.data);
      setMeta(result.meta);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // 읽지 않은 알림이면 읽음 처리
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
          )
        );
        if (meta) {
          setMeta({ ...meta, unreadCount: Math.max(0, meta.unreadCount - 1) });
        }
      } catch (err) {
        console.error('알림 읽음 처리 실패:', err);
      }
    }

    // 리소스 경로가 있으면 해당 페이지로 이동
    const resourcePath = getNotificationResourcePath(notification);
    if (resourcePath) {
      navigate(resourcePath);
    }
  };

  const handleMarkAllRead = async () => {
    setIsMarkingAllRead(true);
    try {
      await markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      if (meta) {
        setMeta({ ...meta, unreadCount: 0 });
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-6 px-4 sm:px-0">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">알림</h1>
          {meta && meta.unreadCount > 0 && (
            <p className="mt-1 text-[15px] font-medium text-ink-500">
              읽지 않은 알림 {meta.unreadCount}개
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* 읽지 않은 알림만 보기 토글 */}
          <label className={CHECK_LABEL_CLASS}>
            <input
              type="checkbox"
              checked={showUnreadOnly}
              onChange={(e) => setShowUnreadOnly(e.target.checked)}
              className={CHECKBOX_CLASS}
            />
            읽지 않은 알림만
          </label>
          {/* 모두 읽음 버튼 */}
          {meta && meta.unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              disabled={isMarkingAllRead}
              className="px-3 py-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
            >
              {isMarkingAllRead ? '처리 중...' : '모두 읽음'}
            </button>
          )}
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="rounded-card border border-rose-200 bg-rose-50 p-4 mb-6">
          <p className="text-sm font-medium text-rose-700">{error}</p>
        </div>
      )}

      {/* 알림 목록 */}
      {notifications.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="text-center py-12">
          <BellIcon className="mx-auto h-12 w-12 text-ink-400" />
          <h3 className="mt-2 text-sm font-medium text-ink-900">
            {showUnreadOnly ? '읽지 않은 알림이 없습니다' : '알림이 없습니다'}
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            새로운 알림이 도착하면 여기에 표시됩니다.
          </p>
        </Card>
      ) : (
        <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
          <ul className="divide-y divide-ink-200">
            {notifications.map((notification) => (
              <li
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                /* 안읽음 강조는 brand로 — blue는 배지가 타입 분류에 쓰는 색이라
                   같은 줄에서 읽음 상태와 타입이 같은 색으로 겹친다. */
                className={`relative hover:bg-ink-50 cursor-pointer transition-colors ${
                  !notification.isRead ? 'bg-brand-50' : ''
                }`}
              >
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-start gap-4">
                    {/* 아이콘 */}
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getNotificationTypeColor(
                        notification.type
                      )}`}
                    >
                      <NotificationIcon type={notification.type} />
                    </div>

                    {/* 내용 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <p
                          className={`text-sm font-medium ${
                            !notification.isRead ? 'text-ink-900' : 'text-ink-600'
                          }`}
                        >
                          {notification.title}
                        </p>
                        <span className="flex-shrink-0 ml-2 text-xs text-ink-500">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-ink-500 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <Badge tone={getNotificationTypeBadgeTone(notification.type)} shape="rounded">
                          {getNotificationTypeLabel(notification.type)}
                        </Badge>
                        {!notification.isRead && (
                          <span className="w-2 h-2 bg-brand-600 rounded-full"></span>
                        )}
                      </div>
                    </div>

                    {/* 화살표 */}
                    {getNotificationResourcePath(notification) && (
                      <ChevronRightIcon className="flex-shrink-0 h-5 w-5 text-ink-400" />
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* 더 불러오기 버튼 */}
      {meta && notifications.length < meta.total && (
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              // 더 불러오기 로직 (페이지네이션)
            }}
            className="text-sm text-brand-600 hover:text-brand-700 font-medium"
          >
            더 보기
          </button>
        </div>
      )}
    </div>
  );
}

// 알림 타입별 아이콘
function NotificationIcon({ type }: { type: string }) {
  switch (type) {
    case 'REPORT_SIGNATURE_REQUEST':
    case 'REPORT_SIGNATURE_REMINDER':
      return <PenIcon className="h-5 w-5" />;
    case 'REPORT_ALL_SIGNED':
      return <CheckIcon className="h-5 w-5" />;
    case 'APPROVAL_REQUEST':
    case 'APPROVAL_COMPLETED':
      return <DocumentIcon className="h-5 w-5" />;
    case 'APPROVAL_REJECTED':
      return <XIcon className="h-5 w-5" />;
    default:
      return <BellIcon className="h-5 w-5" />;
  }
}

// Icons
function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function PenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

